"use client";

import { getSlateSpacingPixels } from "@faire/retailer-shared/SlateMappings/getSlateSpacingPixels";
import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ActionLoggingEventParams,
  ComponentLoggingEventParams,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIGridV2ExpandButton } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/GridV2/SDUIGridV2ExpandButton";
import { SDUIGridV2Tile } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/GridV2/SDUIGridV2Tile";
import {
  getColumnWidth,
  getMaxNumRows,
  getAutoRows,
} from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/GridV2/v2AutoLayoutStylingUtils";
import { SDUIGridHeaderRow } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridHeaderRow";
import {
  DynamicVariable,
  assignInlineVars,
} from "@faire/retailer-visitor-shared/lib/tailwindUtils";
import { Column } from "@faire/web--source/slate/Layout";
import { cn } from "@faire/web--source/tools/cn";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import { IGridStyle } from "@faire/web-api--source/indigofair/data/IGridStyle";
import { IImageTile } from "@faire/web-api--source/indigofair/data/IImageTile";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
import React, { useMemo, useRef, useState } from "react";

const varMobileNumColumns: DynamicVariable<"--f-sdui-grid-nc-m"> = {
  variable: "--f-sdui-grid-nc-m",
  className: "grid-cols-(--f-sdui-grid-nc-m)",
};

const varTabletNumColumns: DynamicVariable<"--f-sdui-grid-nc-md"> = {
  variable: "--f-sdui-grid-nc-md",
  className: "md:grid-cols-(--f-sdui-grid-nc-md)",
};

const varDesktopNumColumns: DynamicVariable<"--f-sdui-grid-nc-lg"> = {
  variable: "--f-sdui-grid-nc-lg",
  className: "lg:grid-cols-(--f-sdui-grid-nc-lg)",
};

const varMobileNumRows: DynamicVariable<"--f-sdui-grid-nr-m"> = {
  variable: "--f-sdui-grid-nr-m",
  className: "grid-rows-(--f-sdui-grid-nr-m)",
};

const varTabletNumRows: DynamicVariable<"--f-sdui-grid-nr-md"> = {
  variable: "--f-sdui-grid-nr-md",
  className: "md:grid-rows-(--f-sdui-grid-nr-md)",
};

const varDesktopNumRows: DynamicVariable<"--f-sdui-grid-nr-lg"> = {
  variable: "--f-sdui-grid-nr-lg",
  className: "lg:grid-rows-(--f-sdui-grid-nr-lg)",
};

const varMobileAutoRows: DynamicVariable<"--f-sdui-grid-ar-m"> = {
  variable: "--f-sdui-grid-ar-m",
  className: "auto-rows-(--f-sdui-grid-ar-m)",
};

const varTabletAutoRows: DynamicVariable<"--f-sdui-grid-ar-md"> = {
  variable: "--f-sdui-grid-ar-md",
  className: "md:auto-rows-(--f-sdui-grid-ar-md)",
};

const varDesktopAutoRows: DynamicVariable<"--f-sdui-grid-ar-lg"> = {
  variable: "--f-sdui-grid-ar-lg",
  className: "lg:auto-rows-(--f-sdui-grid-ar-lg)",
};

export const SDUIGridV2AutoColumnLayoutBase: React.FC<{
  gridComponent: IGridComponent;
  loggingData: ComponentLoggingEventParams;
}> = ({ gridComponent, loggingData }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    responsive_truncation_behaviour,
    style = IGridStyle.build(),
    title,
    responsive_title,
    header_background_icon,
    responsive_header_background_icon,
    tiles,
    truncation_behaviour = IGridComponent.ITruncationBehaviour.build(),
    log_tile_batch_view,
  } = gridComponent;
  const {
    column_spacing = Spacing.SPACING_NONE,
    row_spacing = Spacing.SPACING_NONE,
  } = style;

  const columnSpacingPixels = getSlateSpacingPixels(column_spacing);
  const rowSpacingPixels = getSlateSpacingPixels(row_spacing);

  // TODO (FD-199432): This is an anti-pattern and we generally don't want components reaching into
  // their children's contents to retrieve information. This information should be moved into the proto.
  const imageTileVariant: keyof typeof IImageTile.ImageTileVariant = useMemo(
    () =>
      tiles.find((tile) => tile.image_tile?.style?.variant)?.image_tile?.style
        ?.variant ?? IImageTile.ImageTileVariant.IMAGE_TILE_VARIANT_UNKNOWN,
    [tiles]
  );

  const responsiveTruncationBehaviour =
    convertServerResponsivePropertyToClientResponsiveProperty(
      truncation_behaviour,
      (truncationBehaviour) => truncationBehaviour,
      responsive_truncation_behaviour
    );

  const mobileAndAboveTruncationBehaviour =
    responsiveTruncationBehaviour.mobileAndAbove;
  const tabletAndAboveTruncationBehaviour =
    responsiveTruncationBehaviour.tabletAndAbove ??
    mobileAndAboveTruncationBehaviour;
  const desktopAndAboveTruncationBehaviour =
    responsiveTruncationBehaviour.desktopAndAbove ??
    tabletAndAboveTruncationBehaviour;

  const responsiveTileWidths = getColumnWidth(imageTileVariant);
  const mobileWidth = responsiveTileWidths.mobileAndAbove;
  const tabletWidth = responsiveTileWidths.tabletAndAbove ?? mobileWidth;
  const desktopWidth = responsiveTileWidths.desktopAndAbove ?? tabletWidth;

  return (
    <Column className="w-full">
      <SDUIGridHeaderRow
        title={title}
        responsiveTitle={responsive_title}
        backgroundIcon={header_background_icon}
        responsiveBackgroundIcon={responsive_header_background_icon}
        loggingData={loggingData}
      />
      <div
        className={cn(
          "grid justify-between overflow-hidden",
          varMobileNumColumns.className,
          varTabletNumColumns.className,
          varDesktopNumColumns.className,
          varMobileNumRows.className,
          varTabletNumRows.className,
          varDesktopNumRows.className,
          varMobileAutoRows.className,
          varTabletAutoRows.className,
          varDesktopAutoRows.className
        )}
        ref={containerRef}
        style={{
          columnGap: `${columnSpacingPixels}px`,
          ...assignInlineVars({
            [varMobileNumRows.variable]: getMaxNumRows(
              mobileAndAboveTruncationBehaviour,
              isExpanded
            ),
            [varMobileAutoRows.variable]: getAutoRows(
              mobileAndAboveTruncationBehaviour,
              isExpanded
            ),
            [varTabletNumRows.variable]: getMaxNumRows(
              tabletAndAboveTruncationBehaviour,
              isExpanded
            ),
            [varTabletAutoRows.variable]: getAutoRows(
              tabletAndAboveTruncationBehaviour,
              isExpanded
            ),
            [varDesktopNumRows.variable]: getMaxNumRows(
              desktopAndAboveTruncationBehaviour,
              isExpanded
            ),
            [varDesktopAutoRows.variable]: getAutoRows(
              desktopAndAboveTruncationBehaviour,
              isExpanded
            ),
            [varMobileNumColumns.variable]: `repeat(auto-fill, ${mobileWidth}px)`,
            [varTabletNumColumns.variable]: `repeat(auto-fill, ${tabletWidth}px)`,
            [varDesktopNumColumns.variable]: `repeat(auto-fill, ${desktopWidth}px)`,
          }),
        }}
      >
        {tiles.map((tile, index) => (
          <SDUIGridV2Tile
            key={tile.logging_data?.instance_identifier ?? index}
            tile={tile}
            tileRef={index === 0 ? tileRef : undefined}
            loggingData={loggingData}
            logTileBatchView={log_tile_batch_view}
            rowSpacingPixels={rowSpacingPixels}
          />
        ))}
      </div>
      <SDUIGridV2ExpandButton
        isExpanded={isExpanded}
        truncationBehaviour={responsiveTruncationBehaviour}
        numTiles={tiles.length}
        containerRef={containerRef}
        tileRef={tileRef}
        columnSpacingPixels={columnSpacingPixels}
        rowSpacingPixels={rowSpacingPixels}
        onClick={() => setIsExpanded(true)}
        loggingEventParams={ActionLoggingEventParams.build({
          ...loggingData,
          action_instance_identifier: "EXPAND",
        })}
      />
    </Column>
  );
};
